import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const daysOfWeekFr = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const InteractiveBookingCalendar = ({ 
  salon, 
  selectedService, 
  onTimeSlotSelect, 
  selectedDateTime 
}) => {
  const [currentWeek, setCurrentWeek] = useState(dayjs().startOf('isoWeek')); // Start from Monday (ISO week)
  const [availableSlots, setAvailableSlots] = useState({});
  const [existingBookings, setExistingBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  useEffect(() => {
    if (salon && selectedService) {
      fetchExistingBookings();
    } else if (salon && !selectedService) {
      // Generate slots even without service selected to show available days
      generateAvailableSlots([]);
    }
  }, [salon, selectedService, currentWeek]);

  const fetchExistingBookings = async () => {
    setLoading(true);
    try {
      const weekStart = currentWeek.toDate();
      const weekEnd = currentWeek.add(6, 'day').endOf('day').toDate();

      const q = query(
        collection(db, 'bookings'),
        where('salonId', '==', salon.id),
        where('date', '>=', weekStart),
        where('date', '<=', weekEnd)
      );

      const querySnapshot = await getDocs(q);
      const bookings = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const bookingDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        
        // Filter by status on client side
        if (data.status === 'confirmed' || data.status === 'pending') {
          bookings.push({
            id: doc.id,
            ...data,
            date: bookingDate
          });
        }
      });

      setExistingBookings(bookings);
      generateAvailableSlots(bookings);
    } catch (error) {
      console.error('Error fetching bookings (permissions issue):', error);
      // If we can't fetch bookings due to permissions, generate slots without conflict checking
      console.log('Generating slots without conflict checking due to permission restrictions');
      setExistingBookings([]);
      generateAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableSlots = (bookings) => {
    const slots = {};
    const serviceDuration = selectedService ? parseInt(selectedService.duration) : 60;
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');

    for (let i = 0; i < 7; i++) {
      const date = currentWeek.add(i, 'day');
      const dateStr = date.format('YYYY-MM-DD');
      
      // Fix day mapping: dayjs().day() returns 0=Sunday, 1=Monday, etc.
      let dayIndex = date.day(); // 0=Sunday, 1=Monday, 2=Tuesday, etc.
      let dayKey;
      if (dayIndex === 0) {
        dayKey = 'sunday'; // Sunday
      } else {
        dayKey = daysOfWeek[dayIndex - 1]; // Monday=1 -> index 0, Tuesday=2 -> index 1, etc.
      }

      // Exclure les dates passées ET les dates au-delà de la limite de réservation
      if (date.isBefore(dayjs(), 'day') || date.isAfter(maxDate, 'day')) {
        slots[dateStr] = [];
        continue;
      }

      // Check if salon has schedule and if day is open
      if (!salon.schedule) {
        slots[dateStr] = [];
        continue;
      }

      const daySchedule = salon.schedule[dayKey];
      
      if (!daySchedule || daySchedule.closed) {
        slots[dateStr] = [];
        continue;
      }

      // Parse opening hours
      const openTime = dayjs(`${dateStr} ${daySchedule.open}`);
      const closeTime = dayjs(`${dateStr} ${daySchedule.close}`);
      
      if (!openTime.isValid() || !closeTime.isValid()) {
        slots[dateStr] = [];
        continue;
      }

      const daySlots = [];

      // Generate time slots every 30 minutes
      let currentSlot = openTime;
      let slotCount = 0;
      while (currentSlot.isBefore(closeTime) && slotCount < 20) { // Safety limit
        const slotEnd = currentSlot.add(serviceDuration, 'minute');
        
        // Make sure the service can be completed before closing time
        if (slotEnd.isAfter(closeTime)) {
          break;
        }
        
        // Check if slot conflicts with existing bookings
        const hasConflict = bookings.some(booking => {
          const bookingStart = dayjs(booking.date);
          const bookingService = salon.services?.find(s => s.name === booking.service);
          const bookingDuration = bookingService ? parseInt(bookingService.duration) : 60;
          const bookingEnd = bookingStart.add(bookingDuration, 'minute');

          return (
            (currentSlot.isBefore(bookingEnd) && slotEnd.isAfter(bookingStart))
          );
        });

        // Skip slots that are too close to current time based on salon settings
        const minAdvanceBooking = salon.bookingSettings?.minAdvanceHours || 2; // Default 2 hours
        const isToday = currentSlot.isSame(dayjs(), 'day');
        const isTooSoon = isToday && currentSlot.isBefore(dayjs().add(minAdvanceBooking, 'hour'));

        if (!hasConflict && !isTooSoon) {
          daySlots.push({
            time: currentSlot.format('HH:mm'),
            datetime: currentSlot.toDate(),
            available: true
          });
        }

        currentSlot = currentSlot.add(30, 'minute');
        slotCount++;
      }

      slots[dateStr] = daySlots;
    }

    setAvailableSlots(slots);
  };

  const navigateWeek = (direction) => {
    const newWeek = currentWeek.add(direction * 7, 'day');
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    
    if (direction < 0) {
      // Navigation vers le passé - empêcher si la semaine précédente est entièrement passée
      if (newWeek.add(6, 'day').isBefore(dayjs(), 'day')) {
        return;
      }
    } else {
      // Navigation vers le futur - empêcher si au-delà de la limite de réservation
      if (newWeek.isAfter(maxDate, 'day')) {
        return;
      }
    }
    
    setCurrentWeek(newWeek);
  };

  const handleTimeSlotClick = (date, slot) => {
    if (typeof onTimeSlotSelect === 'function') {
      const selectedDateTime = dayjs(`${date} ${slot.time}`);
      onTimeSlotSelect(selectedDateTime.toDate());
      
      // Scroll to the selected time confirmation after a short delay
      setTimeout(() => {
        const selectedAlert = document.querySelector('[data-testid="selected-time-alert"]');
        if (selectedAlert) {
          selectedAlert.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      }, 100);
    } else {
      console.error('onTimeSlotSelect is not a function:', onTimeSlotSelect);
    }
  };

  const isSlotSelected = (date, slot) => {
    if (!selectedDateTime) return false;
    const slotDateTime = dayjs(`${date} ${slot.time}`);
    const selectedDayjs = dayjs(selectedDateTime);
    return slotDateTime.isSame(selectedDayjs, 'minute');
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(currentWeek.add(i, 'day'));
    }
    return dates;
  };

  const handleDatePickerOpen = () => {
    setSelectedMonth(currentWeek.month());
    setSelectedYear(currentWeek.year());
    setDatePickerOpen(true);
  };

  const handleDatePickerClose = () => {
    setDatePickerOpen(false);
  };

  const handleQuickDateSelect = () => {
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    const newDate = dayjs().month(selectedMonth).year(selectedYear).startOf('month');
    
    // Vérifier si la date sélectionnée est dans les limites
    if (newDate.isBefore(dayjs(), 'month') || newDate.isAfter(maxDate, 'month')) {
      return; // Ne pas permettre de naviguer en dehors des limites
    }
    
    setCurrentWeek(newDate.startOf('isoWeek'));
    setDatePickerOpen(false);
  };

  const getAvailableYears = () => {
    const currentYear = dayjs().year();
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxYear = dayjs().add(maxAdvanceDays, 'day').year();
    const years = [];
    
    for (let year = currentYear; year <= maxYear; year++) {
      years.push(year);
    }
    return years;
  };

  const getAvailableMonths = () => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month();
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    const months = [];
    
    for (let month = 0; month < 12; month++) {
      const testDate = dayjs().month(month).year(selectedYear);
      
      if (selectedYear === currentYear && month < currentMonth) {
        continue; // Skip past months in current year
      }
      
      if (testDate.isAfter(maxDate, 'month')) {
        break; // Stop if beyond max advance date
      }
      
      months.push({
        value: month,
        label: dayjs().month(month).format('MMMM')
      });
    }
    return months;
  };

  if (!selectedService) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Veuillez d'abord sélectionner un service pour voir les créneaux disponibles.
      </Alert>
    );
  }

  if (!salon.schedule) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Les horaires d'ouverture ne sont pas configurés pour ce salon.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <EventAvailableIcon />
          Créneaux disponibles
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() => navigateWeek(-1)}
            disabled={currentWeek.subtract(7, 'day').add(6, 'day').isBefore(dayjs(), 'day')}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Button
            onClick={handleDatePickerOpen}
            sx={{
              textTransform: 'none',
              color: '#000000',
              minWidth: 200,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
            startIcon={<CalendarTodayIcon fontSize="small" />}
          >
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {currentWeek.format('DD MMM')} - {currentWeek.add(6, 'day').format('DD MMM YYYY')}
            </Typography>
          </Button>
          <IconButton
            onClick={() => navigateWeek(1)}
            disabled={currentWeek.add(7, 'day').isAfter(dayjs().add(salon.bookingSettings?.maxAdvanceDays || 30, 'day'), 'day')}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Service sélectionné:</strong> {selectedService.name} ({selectedService.duration} min - {selectedService.price}€)
        </Typography>
      </Alert>

      {selectedDateTime && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            backgroundColor: '#f0f9ff',
            borderColor: '#0ea5e9',
            '& .MuiAlert-icon': {
              color: '#0ea5e9'
            }
          }}
        >
          <Typography variant="body2">
            <strong>Créneau sélectionné:</strong> {dayjs(selectedDateTime).format('dddd DD MMMM YYYY à HH:mm')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6B7280' }}>
            Vous pouvez changer votre sélection en cliquant sur un autre créneau
          </Typography>
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        getWeekDates().filter((date) => {
          const dateStr = date.format('YYYY-MM-DD');
          const daySlots = availableSlots[dateStr] || [];
          const isPastDate = date.isBefore(dayjs(), 'day');
          
          // Toujours afficher le jour qui contient l'heure sélectionnée
          const hasSelectedTime = selectedDateTime &&
            dayjs(selectedDateTime).format('YYYY-MM-DD') === dateStr;
          
          return !isPastDate && (daySlots.length > 0 || hasSelectedTime);
        }).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#6B7280', mb: 2 }}>
              Aucun créneau disponible cette semaine
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              Essayez la semaine suivante en utilisant les flèches de navigation
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            {getWeekDates()
            .filter((date) => {
              const dateStr = date.format('YYYY-MM-DD');
              const daySlots = availableSlots[dateStr] || [];
              const isPastDate = date.isBefore(dayjs(), 'day');
              
              // Toujours afficher le jour qui contient l'heure sélectionnée
              const hasSelectedTime = selectedDateTime &&
                dayjs(selectedDateTime).format('YYYY-MM-DD') === dateStr;
              
              // Ne garder que les jours avec des créneaux disponibles (pas les dates passées)
              // OU le jour qui contient l'heure sélectionnée
              return !isPastDate && (daySlots.length > 0 || hasSelectedTime);
            })
            .map((date, index) => {
              const dateStr = date.format('YYYY-MM-DD');
              const daySlots = availableSlots[dateStr] || [];
              
              // Calculer la hauteur dynamique basée sur le nombre de créneaux
              const dynamicHeight = Math.max(200, Math.min(400, 200 + (daySlots.length * 15)));

              return (
                <Grid item xs={12} sm={6} md key={dateStr}>
                  <Card
                    sx={{
                      minHeight: `${dynamicHeight}px`,
                      backgroundColor: 'white'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        textAlign="center"
                        mb={1}
                        color="text.primary"
                      >
                        {date.format('dddd')}
                      </Typography>
                      <Typography
                        variant="body2"
                        textAlign="center"
                        mb={2}
                        color="text.secondary"
                      >
                        {date.format('DD/MM')}
                      </Typography>

                      <Box display="flex" flexDirection="column" gap={1}>
                        {(() => {
                          // Créer une liste des créneaux à afficher
                          let slotsToShow = [...daySlots];
                          
                          // Si une heure est sélectionnée pour ce jour et qu'elle n'est pas dans la liste
                          if (selectedDateTime && dayjs(selectedDateTime).format('YYYY-MM-DD') === dateStr) {
                            const selectedTime = dayjs(selectedDateTime).format('HH:mm');
                            const hasSelectedSlot = slotsToShow.some(slot => slot.time === selectedTime);
                            
                            if (!hasSelectedSlot) {
                              // Ajouter le créneau sélectionné même s'il n'est plus disponible
                              slotsToShow.push({
                                time: selectedTime,
                                datetime: selectedDateTime,
                                available: false, // Marquer comme non disponible mais sélectionné
                                selected: true
                              });
                              // Trier par heure
                              slotsToShow.sort((a, b) => a.time.localeCompare(b.time));
                            }
                          }
                          
                          return slotsToShow.map((slot, slotIndex) => (
                            <Button
                              key={`${slot.time}-${slotIndex}`}
                              variant={isSlotSelected(dateStr, slot) ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleTimeSlotClick(dateStr, slot)}
                              startIcon={<AccessTimeIcon />}
                              disabled={slot.selected && !slot.available && !isSlotSelected(dateStr, slot)}
                              sx={{
                                justifyContent: 'flex-start',
                                backgroundColor: isSlotSelected(dateStr, slot) ? '#000000' : 'transparent',
                                borderColor: isSlotSelected(dateStr, slot) ? '#000000' : '#f5f5dc',
                                color: isSlotSelected(dateStr, slot) ? '#FFFFFF' : 'text.primary',
                                fontWeight: isSlotSelected(dateStr, slot) ? 600 : 400,
                                boxShadow: isSlotSelected(dateStr, slot) ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                transform: isSlotSelected(dateStr, slot) ? 'scale(1.02)' : 'scale(1)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: isSlotSelected(dateStr, slot) ? '#1F1F1F' : '#e8e8d0',
                                  borderColor: isSlotSelected(dateStr, slot) ? '#1F1F1F' : '#e8e8d0',
                                  transform: 'scale(1.02)'
                                }
                              }}
                            >
                              {slot.time}
                              {isSlotSelected(dateStr, slot) && (
                                <Box component="span" sx={{ ml: 1, fontSize: '0.8rem' }}>
                                  ✓
                                </Box>
                              )}
                            </Button>
                          ));
                        })()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )
      )}

      <Divider sx={{ my: 3 }} />

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#f5f5dc', 
              border: '1px solid #e8e8d0',
              borderRadius: 1 
            }} 
          />
          <Typography variant="body2">Créneau sélectionné</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              border: '1px solid #f5f5dc',
              borderRadius: 1 
            }} 
          />
          <Typography variant="body2">Créneaux disponibles</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#f5f5f5',
              borderRadius: 1 
            }} 
          />
          <Typography variant="body2">Indisponible</Typography>
        </Box>
      </Box>

      {/* Dialog de sélection rapide de date */}
      <Dialog open={datePickerOpen} onClose={handleDatePickerClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aller à un mois spécifique
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Année</InputLabel>
              <Select
                value={selectedYear}
                label="Année"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {getAvailableYears().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Mois</InputLabel>
              <Select
                value={selectedMonth}
                label="Mois"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {getAvailableMonths().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDatePickerClose} sx={{ color: '#6B7280' }}>
            Annuler
          </Button>
          <Button
            onClick={handleQuickDateSelect}
            variant="contained"
            sx={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1F1F1F' }
            }}
          >
            Aller à cette date
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InteractiveBookingCalendar;